import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';

import { Analytics } from '@recruitee/analytics';
import { filterBoolean } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import {
  IconName,
  InputComponent,
  RtPlatform,
  RtTooltip,
  IconComponent,
  IconColorDirective,
  IconButtonComponent,
  OverlayOriginDirective,
  ConnectedOverlayDirective,
} from '@recruitee/user-interface';

import { ViewType } from '../candidates-table/candidates-table.types';
import { SearchPageQueryInputHighlightDirective } from './query-input-highlight.directive';
import { SearchPageQueryInputInfoComponent } from './query-input-info/query-input-info.component';

@Component({
  selector: 'rt-search-page-query-input',
  templateUrl: './query-input.component.html',
  styleUrls: ['./query-input.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.small]': 'isSmall',
  },
  standalone: true,
  imports: [
    IconColorDirective,
    IconComponent,
    OverlayOriginDirective,
    RtTooltip,
    SearchPageQueryInputHighlightDirective,
    InputComponent,
    IconButtonComponent,
    ConnectedOverlayDirective,
    SearchPageQueryInputInfoComponent,
    TranslatePipe,
  ],
})
export class SearchPageQueryInputComponent implements OnChanges {
  @Input() public value: string;
  @Input() public highlight: RegExp = /((\bAND\b|\bNOT\b|\bOR\b)|\(|\)|\")/gi;
  @Input() public view: ViewType;
  @Input() public infoFields: Array<{ icon: IconName; label: string }>;
  @Input() public readMoreLink: string;
  @Input() public customPlaceholderKey: string;
  @Input() public isSmall: boolean = false;

  @Output() public valueChange: EventEmitter<string> = new EventEmitter();

  public dropdownOpened: boolean = false;
  public isFocus: boolean = false;
  public placeholder: string;

  public ViewType: typeof ViewType = ViewType;

  @ViewChild(InputComponent, { static: true }) private inputComponent: InputComponent;
  @ViewChild('inputEl', { static: true }) private inputElement: ElementRef;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    public platform: RtPlatform,
    public analytics: Analytics,
    private cdRef: ChangeDetectorRef,
  ) {}

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit(): void {
    this.placeholder = this.getPlaceholder();

    const debouncedValueChange$: Observable<string> = this.valueChange.pipe(
      debounceTime(500),
      takeUntil(this.ngOnDestroy$.asObservable()),
    );

    debouncedValueChange$.subscribe(value => {
      this.view !== ViewType.AuditLogs &&
        this.analytics.track('[Candidates] FilterBy_InputValue', { length: (value || '').length });
    });

    debouncedValueChange$
      .pipe(
        startWith(this.value),
        filterBoolean,
        map(value => value.match(this.highlight)),
        filterBoolean,
      )
      .subscribe(operators => {
        operators.forEach(operator => {
          // eslint-disable-next-line default-case
          switch (operator.toLowerCase()) {
            case 'and':
              this.view !== ViewType.AuditLogs &&
                this.analytics.track('[Candidates] FilterBy_ANDBooleanOperatorUsed');
              break;

            case 'or':
              this.view !== ViewType.AuditLogs &&
                this.analytics.track('[Candidates] FilterBy_ORBooleanOperatorUsed');
              break;

            case 'not':
              this.view !== ViewType.AuditLogs &&
                this.analytics.track('[Candidates] FilterBy_NOTBooleanOperatorUsed');
              break;
          }
        });
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('view' in changes || 'customPlaceholder' in changes) {
      this.placeholder = this.getPlaceholder();
    }
  }

  public getPlaceholder() {
    if (this.customPlaceholderKey) return this.customPlaceholderKey;

    switch (this.view) {
      case ViewType.Candidates:
        return 'app.new_search_page.placeholders.candidates';
      case ViewType.Notes:
      case ViewType.HireNotes:
        return 'app.new_search_page.placeholders.notes';
      case ViewType.AuditLogs:
        return 'app.settings.audit_logs.search_placeholder';
      case ViewType.Hires:
        return 'app.new_search_page.placeholders.hires';
      default:
        return 'app.new_search_page.placeholders.candidates';
    }
  }

  public toggleDropdown(): void {
    this.dropdownOpened = !this.dropdownOpened;
    this.cdRef.detectChanges();
  }

  public emitValueChange(value: string): void {
    this.valueChange.emit(value);
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public setFocusStatus(status: boolean, event?: FocusEvent): void {
    if (!event?.relatedTarget) {
      this.isFocus = status;
      this.cdRef.detectChanges();
    }
  }

  public setFocus(): void {
    if (this.inputComponent) {
      this.inputComponent.setFocus();
    } else if (this.inputElement && this.inputElement.nativeElement) {
      this.inputElement.nativeElement.focus();
    }
  }
}
